import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSession } from 'next-auth/react';
import Head from 'next/head';
import Image from 'next/image';
import Container from '@material-ui/core/Container';
import { Box, Paper, Skeleton } from '@nfs/ui-kit';
import getConfig from 'next/config';

import styles from './styles.module.scss';
import Footer from '../footer';
import Link from '@/components/link';
import useTheme from '@/hooks/theme';
import TopBannerHiatus from "@/components/layout/top-banner-hiatus";

const { publicRuntimeConfig } = getConfig() ?? {};
const {
    NEXT_PUBLIC_GLOBAL_BANNER_HIATUS_ENABLED
} = publicRuntimeConfig ?? {};

const WEBSITE_TITLE = process.env.NEXT_PUBLIC_WEBSITE_TITLE;

/**
 * Basic layout component
 *
 * @param {*} props
 * @returns
 */
const BasicLayout = props => {
    const { children, description, meta, noFollow, noIndex, title } = props;

    const { data: session } = useSession();
    const { data, loading } = useTheme();

    const { themeName } = data?.tenantByHostname ?? {};

    const logoSrc = themeName
        ? `/themes/${themeName}/logo.png`
        : `/assets/nfs/logo.png`;

    const backgroundImage = themeName
        ? `url(/themes/${themeName}/wallpaper.jpg)`
        : `url(/assets/nfs/wallpaper.jpg)`;

    const [showHiatus, setShowHiatus] = useState();
    const hideTopBannerHiatus = typeof window !== 'undefined'
        ? window.localStorage.getItem(`hideTopBannerHiatus`)
        : false;

    const handleHiatusBannerOnClose = () => {
        window.localStorage.setItem(`hideTopBannerHiatus`, 'true');
        setShowHiatus(false);
    };

    const showTopBannerHiatus =
        NEXT_PUBLIC_GLOBAL_BANNER_HIATUS_ENABLED === 'true' &&
        !hideTopBannerHiatus && showHiatus;

    useEffect(() => {
        if (showHiatus === undefined) {
            setShowHiatus(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hiatusBanner = showTopBannerHiatus ? (
        <TopBannerHiatus
            onClose={handleHiatusBannerOnClose}
        />
    ) : null;

    return (
        <>
            <Head>
                {title && (
                    <title>
                        {WEBSITE_TITLE ? `${title} - ${WEBSITE_TITLE}` : title}
                    </title>
                )}
                {meta}
                {description && (
                    <meta name="description" content={description} />
                )}
                {noFollow && noIndex && (
                    <meta name="robots" content="nofollow,noIndex" />
                )}
                {noFollow && !noIndex && (
                    <meta name="robots" content="nofollow" />
                )}
                {noIndex && !noFollow && (
                    <meta name="robots" content="noindex" />
                )}
            </Head>
            {hiatusBanner}
            <Box
                className={styles.background}
                style={{
                    backgroundImage: !loading && backgroundImage
                }}
                minHeight={1050}
            >
                <Box pt={4} pb={4}>
                    <Container maxWidth="xs">
                        <Paper>
                            <Box p={8}>
                                <Box mb={6}>
                                    <Link
                                        href={Boolean(session) ? '/home' : '/'}
                                    >
                                        <Box
                                            style={{
                                                position: 'relative',
                                                height: '100px'
                                            }}
                                        >
                                            {loading && (
                                                <Skeleton
                                                    variant="rect"
                                                    height={100}
                                                />
                                            )}
                                            {!loading && (
                                                <Image
                                                    src={logoSrc}
                                                    alt="logo"
                                                    layout="fill"
                                                    objectFit="contain"
                                                />
                                            )}
                                        </Box>
                                    </Link>
                                </Box>
                                <main>{children}</main>
                            </Box>
                        </Paper>
                    </Container>
                </Box>
            </Box>
            <Box mt={4}>
                <Footer />
            </Box>
        </>
    );
};

BasicLayout.propTypes = {
    children: PropTypes.node.isRequired,
    description: PropTypes.string,
    meta: PropTypes.node,
    noFollow: PropTypes.bool,
    noIndex: PropTypes.bool,
    title: PropTypes.string
};

BasicLayout.defaultProps = {
    description: null,
    meta: null,
    noFollow: false,
    noIndex: false,
    title: ''
};

export default BasicLayout;
